import React from 'react';
import { FieldController, RadioButton, useMatchScreenWidth } from '@keplerco/core';
import { Datepicker } from '../../../components/inputs/datepicker/datepicker';
import { WidgetCard } from '../../analytics/analytics-filters/widget-card';
import { Control, FieldValues } from 'react-hook-form';

export interface ScheduleWidgetProps {
  control: Control<FieldValues>;
  startDate: Date;
  endDate: Date;
  subtitle?: string;
  title?: string;
  onActivationModeChange?: (isActivateNow: boolean) => void;
}

export function ScheduleWidget({ control, startDate, endDate, subtitle, title, onActivationModeChange }: ScheduleWidgetProps): JSX.Element {
  const isMobile = useMatchScreenWidth('mobile');
  const [activateNow, setActivateNow] = React.useState(!(startDate || endDate));

  function handleActivationModeChange(isActivateNow: boolean) {
    setActivateNow(isActivateNow);

    if (onActivationModeChange) {
      onActivationModeChange(isActivateNow);
    }
  }

  return (
    <WidgetCard title={title ?? 'Schedule'} subtitle={subtitle}>
      <div style={{ display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap', width: isMobile ? '100%' : 300, gap: 8, marginBottom: 16 }}>
        <RadioButton id="activationMode" checked={activateNow} onChange={() => handleActivationModeChange(true)}>
          Activate it now
        </RadioButton>
        <RadioButton id="activationMode" checked={!activateNow} onChange={() => handleActivationModeChange(false)} title="Schedule">
          Schedule for later
        </RadioButton>
      </div>
      {!activateNow && (
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12 }}>
          <FieldController
            name="startDate"
            control={control}
            render={({ field, fieldState }) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              const minDate = new Date(today);
              minDate.setDate(minDate.getDate() - 1);

              const defaultDate = field.value ? new Date(field.value) : undefined;
              const isStartDayInPast = defaultDate && defaultDate < today;
              const isDisabled = subtitle === 'Select end date for this assessment' || isStartDayInPast;

              const maxDate = endDate instanceof Date ? new Date(endDate) : undefined;

              return <Datepicker autoHeight openAbove minDate={minDate} defaultDate={defaultDate} haserror={!!fieldState.error} label="Start date (optional)" maxDate={maxDate} disabled={isDisabled} onDateSelected={value => field.onChange(value)} />;
            }}
          />

          <FieldController
            name="endDate"
            control={control}
            render={({ field, fieldState }) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              const tomorrow = new Date(today);
              tomorrow.setDate(tomorrow.getDate() + 1);

              const defaultDate = field.value ? new Date(field.value) : undefined;

              let minDate: Date | undefined;

              if (startDate instanceof Date) {
                minDate = new Date(startDate);
                minDate.setDate(minDate.getDate());
              } else {
                minDate = tomorrow;
              }
              const isEndDayInPast = endDate && endDate < today;
              const isDisabled = isEndDayInPast;

              // TODO: should be empty if no date chosen
              return <Datepicker autoHeight openAbove defaultDate={defaultDate} haserror={!!fieldState.error} minDate={minDate} label="End date (optional)" disabled={isDisabled} onDateSelected={value => field.onChange(value)} />;
            }}
          />
        </div>
      )}
    </WidgetCard>
  );
}
